'use client';

import { useState } from 'react';
import { GalleryVerticalEnd, ArrowLeft } from "lucide-react"
import Link from 'next/link';
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field"
import { Input } from "@/components/ui/input"
import { useRouter } from 'next/navigation';
import { useAuth } from '@/lib/auth';
import { Checkbox } from "@/components/ui/checkbox"

export function RegisterForm({
  className,
  ...props
}: React.ComponentProps<"div">) {
  const router = useRouter();
  const { registerMerchant } = useAuth();
  const [formData, setFormData] = useState({
    business_name: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [termsError, setTermsError] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = () => {
    if (!agreedToTerms) {
      setTermsError('You must agree to the Terms of Service and Privacy Policy to continue');
      return false;
    }

    if (!formData.business_name || !formData.first_name || !formData.last_name || !formData.email || !formData.phone || !formData.password) {
      setError('Please fill in all required fields');
      return false;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      return false;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setIsLoading(true);
      setError('');
      setSuccess('');
      setTermsError('');

      // Use the registerMerchant function from auth context
      const result = await registerMerchant(
        formData.business_name,
        formData.first_name,
        formData.last_name,
        formData.email,
        formData.phone,
        formData.password
      );

      // Store email in sessionStorage for verification page
      sessionStorage.setItem('pending_verification_email', formData.email);
      sessionStorage.setItem('pending_verification_password', formData.password);

      // Redirect to verification page
      if (result?.requiresVerification) {
        router.push(`/verify-email?email=${encodeURIComponent(formData.email)}`);
      } else {
        // Fallback for old behavior
        router.push('/login');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={cn("w-full", className)} {...props}>
      {/* Enhanced Form Container */}
      <div className="bg-white rounded-3xl shadow-2xl shadow-violet-500/20 border border-violet-100 p-5 backdrop-blur-sm bg-white/95">
        {/* Back to Home Button */}
        <div className="flex justify-start mb-3">
          <Link
            href="/"
            className="inline-flex items-center gap-2 text-sm text-violet-600 hover:text-violet-700 transition-colors font-medium"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Home
          </Link>
        </div>

        <form onSubmit={handleSubmit}>
          <FieldGroup>
            <div className="flex flex-col items-center gap-2 text-center mb-4">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-8 h-8 bg-gradient-to-br from-violet-600 to-fuchsia-600 rounded-xl flex items-center justify-center shadow-lg shadow-violet-500/30">
                  <span className="text-white font-black text-base leading-none select-none" aria-hidden="true">
                    G
                  </span>
                </div>
                <h1 className="text-xl font-bold bg-gradient-to-r from-violet-600 to-fuchsia-600 bg-clip-text text-transparent">
                  GameIt.Lol
                </h1>
              </div>
              <h2 className="text-base font-semibold text-gray-800">Create Your Account</h2>
              <FieldDescription className="text-xs text-gray-600">
                Start your $0 and transform customer engagement
              </FieldDescription>
            </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm p-2.5 rounded-xl mb-3">
              {error}
            </div>
          )}

          {success && (
            <div className="bg-green-50 border border-green-200 text-green-700 text-sm p-2.5 rounded-xl mb-3">
              {success}
            </div>
          )}

          {/* Business Information */}
          <div className="space-y-3 mb-2">
            <div className="space-y-4">
              <Field>
                <FieldLabel htmlFor="business_name" className="text-gray-700 font-medium text-sm">Business Name *</FieldLabel>
                <Input
                  id="business_name"
                  name="business_name"
                  type="text"
                  placeholder="Your business name"
                  value={formData.business_name}
                  onChange={handleInputChange}
                  required
                  disabled={isLoading}
                  className="h-9 border-gray-200 rounded-lg text-black placeholder:text-gray-400 focus:border-violet-500 focus:ring-violet-500/20 transition-all"
                />
              </Field>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field>
                  <FieldLabel htmlFor="first_name" className="text-gray-700 font-medium text-sm">First Name *</FieldLabel>
                  <Input
                    id="first_name"
                    name="first_name"
                    type="text"
                    placeholder="First name"
                    value={formData.first_name}
                    onChange={handleInputChange}
                    required
                    disabled={isLoading}
                    className="h-9 border-gray-200 rounded-lg text-black placeholder:text-gray-400 focus:border-violet-500 focus:ring-violet-500/20 transition-all"
                  />
                </Field>

                <Field>
                  <FieldLabel htmlFor="last_name" className="text-gray-700 font-medium text-sm">Last Name *</FieldLabel>
                  <Input
                    id="last_name"
                    name="last_name"
                    type="text"
                    placeholder="Last name"
                    value={formData.last_name}
                    onChange={handleInputChange}
                    required
                    disabled={isLoading}
                    className="h-9 border-gray-200 rounded-lg text-black placeholder:text-gray-400 focus:border-violet-500 focus:ring-violet-500/20 transition-all"
                  />
                </Field>
              </div>
            </div>

            <Field>
              <FieldLabel htmlFor="email" className="text-gray-700 font-medium text-sm">Email Address *</FieldLabel>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="business@example.com"
                value={formData.email}
                onChange={handleInputChange}
                required
                disabled={isLoading}
                className="h-9 border-gray-200 rounded-lg text-black placeholder:text-gray-400 focus:border-violet-500 focus:ring-violet-500/20 transition-all"
              />
            </Field>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field>
                <FieldLabel htmlFor="phone" className="text-gray-700 font-medium text-sm">Phone Number *</FieldLabel>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  placeholder="+60123456789"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  disabled={isLoading}
                  className="h-9 border-gray-200 rounded-lg text-black placeholder:text-gray-400 focus:border-violet-500 focus:ring-violet-500/20 transition-all"
                />
              </Field>
            </div>
          </div>

          {/* Password Fields */}
          <div className="space-y-2 mb-3">
            <div>
              <h3 className="text-sm font-semibold text-gray-800 mb-2">Account Security</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field>
                  <FieldLabel htmlFor="password" className="text-gray-700 font-medium text-sm">Password *</FieldLabel>
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    placeholder="Create a strong password"
                    value={formData.password}
                    onChange={handleInputChange}
                    required
                    disabled={isLoading}
                    className="h-9 border-gray-200 rounded-lg text-black placeholder:text-gray-400 focus:border-violet-500 focus:ring-violet-500/20 transition-all mb-0"
                  />
                  <FieldDescription className="text-[10px] text-gray-500 mb-0">
                    Min. 8 characters
                  </FieldDescription>
                </Field>

                <Field>
                  <FieldLabel htmlFor="confirmPassword" className="text-gray-700 font-medium text-sm">Confirm Password *</FieldLabel>
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    placeholder="Re-enter your password"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    required
                    disabled={isLoading}
                    className="h-9 border-gray-200 rounded-lg text-black placeholder:text-gray-400 focus:border-violet-500 focus:ring-violet-500/20 transition-all"
                  />
                </Field>
              </div>
            </div>
          </div>

          {/* Terms Agreement Checkbox */}
          <div className="mt-3 pt-3 border-t border-gray-100">
            <div className="flex items-center gap-3">
              <Checkbox
                id="agree-terms"
                checked={agreedToTerms}
                onCheckedChange={(checked) => {
                  setAgreedToTerms(checked === true);
                  setTermsError('');
                }}
                disabled={isLoading}
                className="border-gray-400 bg-white data-[state=checked]:border-violet-600 data-[state=checked]:bg-violet-600"
              />
              <div className="flex-1">
                <label
                  htmlFor="agree-terms"
                  className="text-sm text-gray-600 font-normal cursor-pointer select-none"
                >
                  I have read and agree to the{' '}
                  <Link
                    href="/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-violet-600 hover:text-violet-700 transition-colors font-medium"
                  >
                    Terms of Service
                  </Link>
                  {' '}and{' '}
                  <Link
                    href="/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-violet-600 hover:text-violet-700 transition-colors font-medium"
                  >
                    Privacy Policy
                  </Link>
                </label>
                {termsError && (
                  <p className="text-red-600 text-xs mt-1">{termsError}</p>
                )}
              </div>
            </div>
          </div>

          <Field className="mt-3">
            <Button
              type="submit"
              className="w-full h-9 bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 text-white font-semibold rounded-lg shadow-lg shadow-violet-500/25 hover:shadow-xl hover:shadow-violet-500/30 transition-all duration-200 disabled:opacity-50"
              disabled={isLoading}
            >
              {isLoading ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  Creating Account...
                </div>
              ) : (
                'Create an Account'
              )}
            </Button>
          </Field>

          <div className="mt-2 pt-2 border-t border-gray-100">
            <div className="text-center text-xs text-gray-600">
              Already have an account?{' '}
              <Link
                href="/login"
                className="font-medium text-violet-600 hover:text-violet-700 transition-colors"
              >
                Sign in here
              </Link>
            </div>
          </div>
        </FieldGroup>
      </form>
      </div>
    </div>
  )
}